import React, { useState } from 'react';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { clientToSVG, svgToMetres } from './MapEditorCanvas';
import { DrawAction, DrawSession } from '../drawSession';
import { clamp, Point, RoomRect, SizeMeters } from '../mapGeometry';
import {
    freehandToCorners,
    OUTLINE_MIN_CORNERS,
    outlineEdges,
    rectangleCorners,
    segmentMeasure,
    snapCorner,
} from '../roomOutline';

const MAGENTA = theme.palette.primary.main;
const OUTLINE_FILL = 'rgba(153, 86, 126, 0.08)';
const TOOLTIP_BG = theme.palette.grey['900'];
const TOOLTIP_SECONDARY = theme.palette.grey['400'];

// Sizes in SVG units at 100 % zoom; divided by the zoom so they keep their on-screen size.
const HANDLE_RADIUS = 5;
const ACTIVE_HANDLE_RADIUS = 6.5;
const CLOSING_HANDLE_RADIUS = 8;
const CLOSE_SNAP_RADIUS = 12;
const SEGMENT_HIT_WIDTH = 12;
const OUTLINE_STROKE = 2;
const FONT_SIZE = 11;
// Approximate advance of one character at FONT_SIZE; SVG text has no layout to size a box from.
const CHAR_WIDTH = 6.2;
const BOLD_CHAR_WIDTH = 6.8;
const EDGE_LABEL_GAP = 6;
const TOOLTIP_OFFSET = 12;
const TOOLTIP_PADDING = 8;
const TOOLTIP_LINE_HEIGHT = 16;
// The capture surface must cover the whole viewBox at 25 % zoom, pan included.
const CAPTURE_EXTENT = 10000;
const FREEHAND_MIN_STEP_METERS = 0.05;

interface CornerTooltipData {
    corner: Point;
    number: number;
    previous: Point | null;
}

interface RoomDrawLayerProps {
    session: DrawSession;
    dispatch: React.Dispatch<DrawAction>;
    room: RoomRect;
    mapSize: SizeMeters;
    zoom: number;
    svgRef: React.RefObject<SVGSVGElement>;
}

function toPx(p: Point, room: RoomRect): Point {
    return { x: room.x + p.x * room.pxPerMeter, y: room.y + p.y * room.pxPerMeter };
}

function pointsAttr(points: Point[], room: RoomRect): string {
    return points
        .map((p) => toPx(p, room))
        .map((p) => `${p.x},${p.y}`)
        .join(' ');
}

export default function RoomDrawLayer({
    session,
    dispatch,
    room,
    mapSize,
    zoom,
    svgRef,
}: RoomDrawLayerProps) {
    const { outline, tool, selection, snapToGrid, snapToAngle } = session;
    const { corners, closed } = outline;
    const unit = 1 / zoom;
    const snapOptions = { grid: snapToGrid, angle: snapToAngle };
    const gridOnly = { grid: snapToGrid, angle: false };

    const [hoverPoint, setHoverPoint] = useState<Point | null>(null);
    const [rectDrag, setRectDrag] = useState<{ anchor: Point; end: Point } | null>(null);
    const [stroke, setStroke] = useState<Point[] | null>(null);
    const [vertexDrag, setVertexDrag] = useState<{ index: number; point: Point } | null>(null);

    const lastCorner = corners.length > 0 ? corners[corners.length - 1] : null;
    const canClose = !closed && corners.length >= OUTLINE_MIN_CORNERS;

    function pointerToMetres(e: React.PointerEvent): Point {
        if (!svgRef.current) return { x: 0, y: 0 };
        const m = svgToMetres(clientToSVG(svgRef.current, e.clientX, e.clientY), room);
        return { x: clamp(m.x, 0, mapSize.width), y: clamp(m.y, 0, mapSize.height) };
    }

    function nextPolylinePoint(raw: Point): Point {
        const first = corners[0];
        const pxToFirst = first
            ? Math.hypot(raw.x - first.x, raw.y - first.y) * room.pxPerMeter
            : Infinity;
        if (canClose && pxToFirst <= CLOSE_SNAP_RADIUS * unit) return first;
        return snapCorner(raw, lastCorner, snapOptions, mapSize);
    }

    function previousCorner(index: number): Point | null {
        if (index > 0) return corners[index - 1];
        return closed ? corners[corners.length - 1] : null;
    }

    function handleSurfaceDown(e: React.PointerEvent<SVGRectElement>) {
        if (e.button !== 0) return;
        const raw = pointerToMetres(e);
        if (tool === 'edit') {
            dispatch({ type: 'SELECT', payload: null });
            return;
        }
        if (tool === 'polyline') {
            if (closed) return;
            const point = nextPolylinePoint(raw);
            const closesRoom = canClose && point.x === corners[0].x && point.y === corners[0].y;
            if (closesRoom) dispatch({ type: 'CLOSE' });
            else dispatch({ type: 'ADD_CORNER', payload: point });
            return;
        }
        e.currentTarget.setPointerCapture(e.pointerId);
        if (tool === 'rectangle') {
            const anchor = snapCorner(raw, null, gridOnly, mapSize);
            setRectDrag({ anchor, end: anchor });
        } else {
            setStroke([raw]);
        }
    }

    function handleSurfaceMove(e: React.PointerEvent<SVGRectElement>) {
        const raw = pointerToMetres(e);
        if (rectDrag) {
            setRectDrag({ ...rectDrag, end: snapCorner(raw, null, gridOnly, mapSize) });
            return;
        }
        if (stroke) {
            const prev = stroke[stroke.length - 1];
            if (Math.hypot(raw.x - prev.x, raw.y - prev.y) >= FREEHAND_MIN_STEP_METERS) {
                setStroke([...stroke, raw]);
            }
            return;
        }
        setHoverPoint(tool === 'polyline' && !closed ? nextPolylinePoint(raw) : null);
    }

    function handleSurfaceUp() {
        if (rectDrag) {
            const { anchor, end } = rectDrag;
            setRectDrag(null);
            if (anchor.x === end.x || anchor.y === end.y) return;
            dispatch({
                type: 'REPLACE_OUTLINE',
                payload: { corners: rectangleCorners(anchor, end), closed: true },
            });
            return;
        }
        if (stroke) {
            const drawn = freehandToCorners(stroke, snapToGrid, mapSize);
            setStroke(null);
            // Too short to close stays an open chain, so the Corners table shows the "at least 3" error.
            dispatch({
                type: 'REPLACE_OUTLINE',
                payload: { corners: drawn, closed: drawn.length >= OUTLINE_MIN_CORNERS },
            });
        }
    }

    function handleVertexDown(index: number, e: React.PointerEvent<SVGCircleElement>) {
        e.stopPropagation();
        if (tool === 'polyline') {
            dispatch({ type: 'CLOSE' });
            return;
        }
        e.currentTarget.setPointerCapture(e.pointerId);
        dispatch({ type: 'SELECT', payload: { kind: 'vertex', index } });
        setVertexDrag({ index, point: corners[index] });
    }

    function handleVertexMove(e: React.PointerEvent<SVGCircleElement>) {
        if (!vertexDrag) return;
        const point = snapCorner(
            pointerToMetres(e),
            previousCorner(vertexDrag.index),
            snapOptions,
            mapSize
        );
        setVertexDrag({ index: vertexDrag.index, point });
    }

    function handleVertexUp() {
        if (!vertexDrag) return;
        dispatch({ type: 'MOVE_CORNER', payload: vertexDrag });
        setVertexDrag(null);
    }

    const shownCorners = vertexDrag
        ? corners.map((c, i) => (i === vertexDrag.index ? vertexDrag.point : c))
        : corners;

    const hoverClosesRoom =
        hoverPoint !== null &&
        canClose &&
        hoverPoint.x === corners[0].x &&
        hoverPoint.y === corners[0].y;
    const selectedIndex =
        tool === 'edit' && selection?.kind === 'vertex' && selection.index < corners.length
            ? selection.index
            : null;
    const activeIndex = vertexDrag?.index ?? selectedIndex ?? (hoverClosesRoom ? 0 : null);

    let tooltip: CornerTooltipData | null = null;
    if (vertexDrag) {
        tooltip = {
            corner: vertexDrag.point,
            number: vertexDrag.index + 1,
            previous: previousCorner(vertexDrag.index),
        };
    } else if (selectedIndex !== null) {
        tooltip = {
            corner: corners[selectedIndex],
            number: selectedIndex + 1,
            previous: previousCorner(selectedIndex),
        };
    } else if (hoverPoint) {
        tooltip = {
            corner: hoverPoint,
            number: hoverClosesRoom ? 1 : corners.length + 1,
            previous: lastCorner,
        };
    }
    // The last rendered viewBox bounds the tooltip; the pan drag updates it imperatively.
    const viewBox = tooltip ? svgRef.current?.viewBox.baseVal : undefined;

    const isDrawingTool = tool !== 'edit';

    return (
        <g>
            <rect
                x={-CAPTURE_EXTENT}
                y={-CAPTURE_EXTENT}
                width={CAPTURE_EXTENT * 2}
                height={CAPTURE_EXTENT * 2}
                fill="transparent"
                style={{ cursor: isDrawingTool ? 'crosshair' : 'default' }}
                onPointerDown={handleSurfaceDown}
                onPointerMove={handleSurfaceMove}
                onPointerUp={handleSurfaceUp}
                onPointerLeave={() => setHoverPoint(null)}
            />

            {closed ? (
                <polygon
                    points={pointsAttr(shownCorners, room)}
                    fill={OUTLINE_FILL}
                    stroke={MAGENTA}
                    strokeWidth={OUTLINE_STROKE * unit}
                    strokeLinejoin="round"
                    pointerEvents="none"
                />
            ) : (
                <polyline
                    points={pointsAttr(shownCorners, room)}
                    fill="none"
                    stroke={MAGENTA}
                    strokeWidth={OUTLINE_STROKE * unit}
                    strokeLinejoin="round"
                    pointerEvents="none"
                />
            )}

            {hoverPoint && lastCorner && (
                <SegmentLine a={lastCorner} b={hoverPoint} room={room} unit={unit} dashed />
            )}
            {rectDrag && (
                <polygon
                    points={pointsAttr(rectangleCorners(rectDrag.anchor, rectDrag.end), room)}
                    fill={OUTLINE_FILL}
                    stroke={MAGENTA}
                    strokeWidth={OUTLINE_STROKE * unit}
                    strokeDasharray={`${4 * unit} ${3 * unit}`}
                    pointerEvents="none"
                />
            )}
            {stroke && (
                <polyline
                    points={pointsAttr(stroke, room)}
                    fill="none"
                    stroke={MAGENTA}
                    strokeWidth={OUTLINE_STROKE * unit}
                    pointerEvents="none"
                />
            )}

            {tool === 'edit' &&
                closed &&
                shownCorners.map((a, i) => {
                    const b = shownCorners[(i + 1) % shownCorners.length];
                    const isSelected = selection?.kind === 'segment' && selection.index === i;
                    return (
                        <g key={`segment-${i}`}>
                            {isSelected && (
                                <SegmentLine a={a} b={b} room={room} unit={unit} highlighted />
                            )}
                            <line
                                x1={toPx(a, room).x}
                                y1={toPx(a, room).y}
                                x2={toPx(b, room).x}
                                y2={toPx(b, room).y}
                                stroke="transparent"
                                strokeWidth={SEGMENT_HIT_WIDTH * unit}
                                style={{ cursor: 'pointer' }}
                                onPointerDown={(e) => {
                                    e.stopPropagation();
                                    dispatch({
                                        type: 'SELECT',
                                        payload: { kind: 'segment', index: i },
                                    });
                                }}
                            />
                        </g>
                    );
                })}

            {outlineEdges(shownCorners, closed).map(({ a, b, length, normal }, i) => {
                const label = i18n.t('tableMaps.wizard.draw.edgeLength', {
                    length: length.toFixed(2),
                });
                const labelWidth = label.length * BOLD_CHAR_WIDTH;
                // Too short on screen to hold its label: the tooltip still gives the length.
                if (length * room.pxPerMeter * zoom < labelWidth + EDGE_LABEL_GAP * 2) return null;
                const pa = toPx(a, room);
                const pb = toPx(b, room);
                const offset =
                    EDGE_LABEL_GAP +
                    Math.abs(normal.x) * (labelWidth / 2) +
                    Math.abs(normal.y) * (FONT_SIZE / 2);
                return (
                    <text
                        key={`edge-${i}`}
                        x={(pa.x + pb.x) / 2 + normal.x * offset * unit}
                        y={(pa.y + pb.y) / 2 + normal.y * offset * unit}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={FONT_SIZE * unit}
                        fontWeight={600}
                        fill={MAGENTA}
                        stroke="white"
                        strokeWidth={3 * unit}
                        paintOrder="stroke"
                        pointerEvents="none"
                    >
                        {label}
                    </text>
                );
            })}

            {hoverPoint && !hoverClosesRoom && (
                <circle
                    cx={toPx(hoverPoint, room).x}
                    cy={toPx(hoverPoint, room).y}
                    r={ACTIVE_HANDLE_RADIUS * unit}
                    fill={MAGENTA}
                    pointerEvents="none"
                />
            )}

            {shownCorners.map((corner, i) => {
                const px = toPx(corner, room);
                const isClosingHandle = i === 0 && canClose;
                const isActive = i === activeIndex;
                const isInteractive = tool === 'edit' || (tool === 'polyline' && isClosingHandle);
                const radius = isClosingHandle
                    ? CLOSING_HANDLE_RADIUS
                    : isActive
                    ? ACTIVE_HANDLE_RADIUS
                    : HANDLE_RADIUS;
                return (
                    <circle
                        key={`corner-${i}`}
                        cx={px.x}
                        cy={px.y}
                        r={radius * unit}
                        fill={isActive ? MAGENTA : 'white'}
                        stroke={MAGENTA}
                        strokeWidth={OUTLINE_STROKE * unit}
                        pointerEvents={isInteractive ? 'all' : 'none'}
                        style={{ cursor: tool === 'edit' ? 'move' : 'pointer' }}
                        onPointerDown={(e) => handleVertexDown(i, e)}
                        onPointerMove={handleVertexMove}
                        onPointerUp={handleVertexUp}
                    />
                );
            })}

            {!closed && corners.length >= 2 && (
                <text
                    x={toPx(corners[0], room).x}
                    y={toPx(corners[0], room).y + (CLOSING_HANDLE_RADIUS + FONT_SIZE + 2) * unit}
                    textAnchor="middle"
                    fontSize={FONT_SIZE * unit}
                    fontWeight={600}
                    fill={MAGENTA}
                    pointerEvents="none"
                >
                    {i18n.t('tableMaps.wizard.draw.closingPoint')}
                </text>
            )}

            {tooltip && (
                <CornerTooltip
                    {...tooltip}
                    room={room}
                    unit={unit}
                    bounds={viewBox && viewBox.width > 0 ? viewBox : null}
                />
            )}
        </g>
    );
}

function SegmentLine({
    a,
    b,
    room,
    unit,
    dashed = false,
    highlighted = false,
}: {
    a: Point;
    b: Point;
    room: RoomRect;
    unit: number;
    dashed?: boolean;
    highlighted?: boolean;
}) {
    const pa = toPx(a, room);
    const pb = toPx(b, room);
    return (
        <line
            x1={pa.x}
            y1={pa.y}
            x2={pb.x}
            y2={pb.y}
            stroke={MAGENTA}
            strokeWidth={(highlighted ? 5 : OUTLINE_STROKE) * unit}
            strokeOpacity={highlighted ? 0.5 : 1}
            strokeDasharray={dashed ? `${4 * unit} ${3 * unit}` : undefined}
            strokeLinecap="round"
            pointerEvents="none"
        />
    );
}

function CornerTooltip({
    corner,
    number,
    previous,
    room,
    unit,
    bounds,
}: CornerTooltipData & {
    room: RoomRect;
    unit: number;
    bounds: { x: number; y: number; width: number; height: number } | null;
}) {
    const title = i18n.t('tableMaps.wizard.draw.cornerTooltip', {
        number,
        x: corner.x.toFixed(2),
        y: corner.y.toFixed(2),
    });
    const edge = previous ? segmentMeasure(previous, corner) : null;
    const detail = edge
        ? i18n.t('tableMaps.wizard.draw.edgeTooltip', {
              length: edge.length.toFixed(2),
              angle: edge.angleToHorizontal.toFixed(1),
          })
        : null;
    const width =
        (Math.max(title.length * BOLD_CHAR_WIDTH, (detail?.length ?? 0) * CHAR_WIDTH) +
            TOOLTIP_PADDING * 2) *
        unit;
    const height = ((detail ? 2 : 1) * TOOLTIP_LINE_HEIGHT + TOOLTIP_PADDING) * unit;
    const anchor = toPx(corner, room);
    let x = anchor.x + TOOLTIP_OFFSET * unit;
    let y = anchor.y + TOOLTIP_OFFSET * unit;
    if (bounds && x + width > bounds.x + bounds.width) x = anchor.x - TOOLTIP_OFFSET * unit - width;
    if (bounds && y + height > bounds.y + bounds.height) {
        y = anchor.y - TOOLTIP_OFFSET * unit - height;
    }
    const textX = x + TOOLTIP_PADDING * unit;
    const firstBaseline = y + (TOOLTIP_PADDING / 2 + TOOLTIP_LINE_HEIGHT - 4) * unit;
    return (
        <g pointerEvents="none">
            <rect x={x} y={y} width={width} height={height} rx={4 * unit} fill={TOOLTIP_BG} />
            <text
                x={textX}
                y={firstBaseline}
                fontSize={FONT_SIZE * unit}
                fontWeight={600}
                fill="white"
                xmlSpace="preserve"
            >
                {title}
            </text>
            {detail && (
                <text
                    x={textX}
                    y={firstBaseline + TOOLTIP_LINE_HEIGHT * unit}
                    fontSize={FONT_SIZE * unit}
                    fill={TOOLTIP_SECONDARY}
                >
                    {detail}
                </text>
            )}
        </g>
    );
}
